import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { LoginFormData } from '../types/auth';
import { loginSchema } from '../utils/validation';
import { useAuth } from './useAuth';

export const useLogin = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    code?: string;
    email?: string;
  } | null>(null);
  const { login, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema) as any,
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const watchedValues = watch();

  const onSubmit = async (data: LoginFormData) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    clearError();
    setErrorDetails(null);

    try {
      await login(
        {
          email: data.email,
          password: data.password,
        },
        data.rememberMe,
        'student'
      );

      // Redirect will be handled by PublicRoute component in App.tsx
      // based on user role after successful login
      // No explicit navigation needed here as the route protection will handle it
    } catch (error: any) {
      // Check for EMAIL_NOT_VERIFIED error
      if (error.response?.data?.error?.code === 'EMAIL_NOT_VERIFIED') {
        setErrorDetails({
          code: 'EMAIL_NOT_VERIFIED',
          email: data.email,
        });
      }
      console.error('Login submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldFocus = (fieldName: keyof LoginFormData) => {
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
    if (authError) {
      clearError();
    }
  };

  const resetForm = () => {
    setValue('email', '');
    setValue('password', '');
    setValue('rememberMe', false);
    clearErrors();
    clearError();
  };

  return {
    // Form state
    register,
    handleSubmit: handleSubmit(onSubmit as any),
    errors,
    isValid,
    watchedValues,

    // Submission state
    isSubmitting,
    authError,
    errorDetails,

    // Form actions
    handleFieldFocus,
    resetForm,
    setValue,
    clearError,

    // Field validation helpers
    hasFieldError: (field: keyof LoginFormData) => !!errors[field],
    getFieldError: (field: keyof LoginFormData) => errors[field]?.message,
  };
};

// Hook for "Remember Me" functionality
export const useRememberMe = () => {
  const [rememberMe, setRememberMe] = useState(false);

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
  };

  return {
    rememberMe,
    handleRememberMeChange,
  };
};
